import { Close as CloseIcon } from '@mui/icons-material';
import { Alert, Box, IconButton, Typography, useTheme } from '@mui/material';
import React, { useMemo } from 'react';

import {
  CAMPAIGN_HAS_ITEMS_FROM_DIFFERENT_FC_WARNING,
  INVENTORY_OUT_OF_STOCK_WARNING,
  MSKU_HAS_ITEM_WITH_NO_PREORDER_MESSAGE,
} from '../../../constants/inventories';
import { ICampaignItem } from '../../../types/campaigns';
import { isCampaignEligibleToOrder } from '../../../utils/campaigns';
import { hasMskuAnyOutOfStockOptions, isSameFC } from '../../../utils/inventories';

interface IProps {
  title: string;
  onClose: () => void;
  items: ICampaignItem[];
}

export const SidebarHeader: React.FC<IProps> = ({ title, onClose, items }) => {
  const theme = useTheme();

  const hasItemsFromDifferentFC = useMemo(() => (items ? !isSameFC(items) : false), [items]);

  const isCampaignNotEligibleToOrder = useMemo(() => !isCampaignEligibleToOrder(items as ICampaignItem[]), [items]);

  const sidebarMessage = useMemo(() => {
    if (isCampaignNotEligibleToOrder) {
      return (
        <Alert severity="error" key="outOfStock">
          {INVENTORY_OUT_OF_STOCK_WARNING}
        </Alert>
      );
    }

    if (hasMskuAnyOutOfStockOptions(items)) {
      return (
        <Alert severity="warning" key="mskuDisabled">
          {MSKU_HAS_ITEM_WITH_NO_PREORDER_MESSAGE}
        </Alert>
      );
    }

    return null;
  }, [items, isCampaignNotEligibleToOrder]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 3, px: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ lineHeight: 1.4, color: theme.sass.ebony }}>
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="close"
          size="small"
          sx={{ padding: '5px' }}
          data-action="close_sidebar_btn"
        >
          <CloseIcon fontSize="small" sx={{ width: 20, height: 20 }} />
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {hasItemsFromDifferentFC && <Alert severity="error">{CAMPAIGN_HAS_ITEMS_FROM_DIFFERENT_FC_WARNING}</Alert>}
        {sidebarMessage}
      </Box>
    </Box>
  );
};
